/**
 * UTILITAIRE CENTRALISÉ POUR LA GESTION DES DATES - VERSION FRONTEND
 * 
 * Ce module fournit des fonctions utilitaires pour :
 * - Formater les dates de manière cohérente avec le backend
 * - Valider les plages de dates
 * - Convertir les dates entre formats
 * - Gérer les fuseaux horaires
 * 
 * IMPORTANT: Doit rester synchronisé avec backend/utils/dateUtils.js
 */

/**
 * Formate une date au format YYYY-MM-DD (ISO date string sans time)
 * IDENTIQUE au backend pour éviter les décalages de timezone
 */
export const formatToDateString = (date) => {
  if (!date) return null;
  
  // Si c'est déjà une string au bon format
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // Si c'est un objet Date ou une string parsable
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    console.warn('[DateUtils] Invalid date provided:', date);
    return null;
  }
  
  // Utiliser les fonctions locales pour éviter les problèmes de timezone
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Parse une date string vers un objet Date
 * Gère différents formats d'entrée
 */
export const parseToDate = (dateString) => {
  if (!dateString) return null;
  
  // Si c'est déjà un objet Date
  if (dateString instanceof Date) {
    return dateString;
  }
  
  // Si c'est une string au format YYYY-MM-DD, créer une date locale
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month - 1 car les mois commencent à 0
  }
  
  // Sinon, utiliser le constructor Date standard
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Vérifie si une date de fin est après une date de début
 */
export const isValidDateRange = (startDate, endDate) => {
  const start = parseToDate(startDate);
  const end = parseToDate(endDate);
  
  if (!start || !end) return false;
  
  return end > start;
};

/**
 * Vérifie si deux périodes se chevauchent
 */
export const periodsOverlap = (start1, end1, start2, end2) => {
  const s1 = parseToDate(start1);
  const e1 = parseToDate(end1);
  const s2 = parseToDate(start2);
  const e2 = parseToDate(end2);
  
  if (!s1 || !e1 || !s2 || !e2) return false;
  
  // Deux périodes se chevauchent si :
  // - La fin de la première est après le début de la seconde ET
  // - Le début de la première est avant la fin de la seconde
  return e1 >= s2 && s1 <= e2;
};

/**
 * Convertit une plage saisie côté UI [start, end inclus] en plage stockée [start, end exclusif)
 */
export const toExclusiveAvailabilityRange = (startDate, endDateInclusive) => {
  const availableFrom = formatToDateString(startDate);
  const inclusiveEnd = formatToDateString(endDateInclusive);

  if (!availableFrom || !inclusiveEnd) {
    return {
      available_from: null,
      available_to: null,
      startDate: null,
      endDate: null,
    };
  }

  const availableTo = addDays(inclusiveEnd, 1);

  return {
    available_from: availableFrom,
    available_to: availableTo,
    startDate: parseToDate(availableFrom),
    endDate: parseToDate(availableTo),
  };
};

/**
 * Convertit une borne de fin exclusive stockée en dernière date réellement occupée
 */
export const getInclusiveAvailabilityEnd = (endDateExclusive) => {
  const exclusiveEnd = formatToDateString(endDateExclusive);
  if (!exclusiveEnd) return null;
  return addDays(exclusiveEnd, -1);
};

/**
 * Vérifie si une date appartient à une plage de disponibilité stockée en end-exclusive
 */
export const isDateWithinAvailabilityRange = (date, startDate, endDateExclusive) => {
  const current = formatToDateString(date);
  const start = formatToDateString(startDate);
  const endExclusive = formatToDateString(endDateExclusive);

  if (!current || !start || !endExclusive) return false;

  return current >= start && current < endExclusive;
};

/**
 * Vérifie le chevauchement de deux plages stockées en end-exclusive
 */
export const availabilityRangesOverlap = (start1, endExclusive1, start2, endExclusive2) => {
  const s1 = formatToDateString(start1);
  const e1 = formatToDateString(endExclusive1);
  const s2 = formatToDateString(start2);
  const e2 = formatToDateString(endExclusive2);

  if (!s1 || !e1 || !s2 || !e2) return false;

  return s1 < e2 && e1 > s2;
};

/**
 * Calcule la durée réelle occupée d'une plage stockée en end-exclusive
 */
export const getAvailabilityDurationInDays = (startDate, endDateExclusive) => {
  const start = parseToDate(startDate);
  const end = parseToDate(endDateExclusive);

  if (!start || !end) return 0;

  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Normalise les champs de date dans un objet
 * Convertit toutes les dates au format string YYYY-MM-DD
 */
export const normalizeDateFields = (obj) => {
  const normalized = { ...obj };
  
  // Liste des champs qui sont des dates
  const dateFields = [
    'startDate', 'endDate', 
    'available_from', 'available_to',
    'date_from', 'date_to'
  ];
  
  dateFields.forEach(field => {
    if (normalized[field]) {
      normalized[field] = formatToDateString(normalized[field]);
    }
  });
  
  return normalized;
};

/**
 * Valide qu'une string est une date valide
 */
export const isValidDateString = (dateString) => {
  if (!dateString) return false;
  
  // Vérifier le format YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return false;
  
  // Vérifier que la date est parsable et valide
  const date = parseToDate(dateString);
  if (!date) return false;
  
  // Vérifier que la date reformatée correspond à l'entrée
  // (évite les dates comme 2023-02-30 qui deviennent 2023-03-02)
  return formatToDateString(date) === dateString;
};

/**
 * Convertit une date Material UI vers notre format
 */
export const convertMuiDate = (muiDate) => {
  if (!muiDate) return null;
  
  // MUI date picker renvoie un objet dayjs ou moment
  if (muiDate && typeof muiDate.format === 'function') {
    return muiDate.format('YYYY-MM-DD');
  }
  
  // Si c'est un objet Date standard
  return formatToDateString(muiDate);
};

/**
 * Prépare les données de période pour l'envoi à l'API
 */
export const preparePeriodForApi = (period) => {
  const prepared = {
    ...period,
    available_from: formatToDateString(period.available_from || period.startDate),
    available_to: formatToDateString(period.available_to || period.endDate)
  };
  
  // Nettoyer les champs qui ne sont pas nécessaires pour l'API
  delete prepared.startDate;
  delete prepared.endDate;
  
  return prepared;
};

/**
 * Debug: Affiche les informations de dates dans la console
 */
export const debugDates = (label, dates) => {
  console.log(`🔍 [DateUtils] ${label}:`, {
    ...dates,
    formatted: Object.keys(dates).reduce((acc, key) => {
      acc[key] = formatToDateString(dates[key]);
      return acc;
    }, {})
  });
};

/**
 * Constantes pour les formats de date
 */
export const DATE_FORMATS = {
  API: 'YYYY-MM-DD',           // Format pour l'API
  DISPLAY: 'DD/MM/YYYY',       // Format d'affichage pour l'utilisateur
  ISO: 'YYYY-MM-DDTHH:mm:ss.sssZ'  // Format ISO complet
};

/**
 * Obtient la date d'aujourd'hui au format YYYY-MM-DD
 */
export const getTodayString = () => {
  return formatToDateString(new Date());
};

/**
 * Ajoute/soustrait des jours à une date
 */
export const addDays = (date, days) => {
  const result = parseToDate(date);
  if (!result) return null;
  
  result.setDate(result.getDate() + days);
  return formatToDateString(result);
};

// Export par défaut avec toutes les fonctions
export default {
  formatToDateString,
  parseToDate,
  isValidDateRange,
  periodsOverlap,
  toExclusiveAvailabilityRange,
  getInclusiveAvailabilityEnd,
  isDateWithinAvailabilityRange,
  availabilityRangesOverlap,
  getAvailabilityDurationInDays,
  normalizeDateFields,
  isValidDateString,
  convertMuiDate,
  preparePeriodForApi,
  debugDates,
  DATE_FORMATS,
  getTodayString,
  addDays
};
